import type { ScanResult, VisibilityAnalysis } from "@/lib/api/ai-visibility";
import type { PlatformVisibilityResult, PlatformResult } from "@/lib/api/platform-visibility";
import type { GoogleSearchResult } from "@/lib/api/google-search-visibility";

/**
 * Mock data for Debug Mode testing
 * Used when ?debug=true to avoid costly API calls during UI development
 */

export const mockAnalysis: VisibilityAnalysis = {
  visibilityScore: 65,
  keyFindings: [
    "🐛 DEBUG MODE - Mock data for testing",
    "Brand appears in AI recommendations for target keyword",
    "Strong presence in AI-generated product comparisons",
    "Mentioned alongside 2 major competitors"
  ],
  competitorMentions: [
    { name: "Competitor Alpha", context: "Often mentioned as alternative solution" },
    { name: "Competitor Beta", context: "Referenced in feature comparisons" }
  ],
  recommendations: [
    "Increase content depth around key product features",
    "Build more authoritative backlinks from industry publications",
    "Create more FAQ-style content that AI can easily reference"
  ],
  summary: "🐛 DEBUG MODE - This is mock scan data for testing UI without API costs. Your brand shows moderate visibility in AI responses."
};

// V2.1 Discovery Mode mock data
export const mockV21DiscoveryData = {
  aiScore: 40,
  queryType: 'discovery' as const,
  queryUsed: 'Best running shoes',
  rawResponseSnippet: '🐛 DEBUG MOCK: When looking for the best running shoes, Nike consistently ranks among the top recommendations. The Nike Air Zoom Pegasus and Nike ZoomX Vaporfly are particularly well-regarded for their performance and comfort...'
};

export const mockPlatforms: PlatformResult[] = [
  {
    platform: "ChatGPT",
    found: true,
    context: "🐛 MOCK: Brand mentioned in product recommendations",
    confidence: "high",
    competitors: ["Competitor Alpha", "Competitor Beta"]
  },
  {
    platform: "Google Search",
    found: true,
    context: "🐛 MOCK: Appears in top 10 organic results",
    confidence: "medium",
    competitors: ["Competitor Alpha", "Competitor Gamma"]
  }
];

/**
 * Creates mock AI scan result with provided brand and keyword
 * V2.1: Includes Discovery Mode fields
 */
export function createMockAIResult(brandName: string, targetKeyword: string): ScanResult {
  // Build discovery query with keyword hygiene (same as backend)
  const keywordLower = targetKeyword.toLowerCase().trim();
  const startsWithBestOrTop = keywordLower.startsWith('best ') || keywordLower.startsWith('top ');
  const queryUsed = startsWithBestOrTop ? targetKeyword.trim() : `Best ${targetKeyword.trim()}`;
  
  return {
    success: true,
    brandName,
    targetKeyword,
    analysis: mockAnalysis,
    // V2.1 Discovery Mode fields
    aiScore: 40,
    queryType: 'discovery',
    queryUsed: queryUsed,
    rawResponseSnippet: `🐛 DEBUG MOCK: When searching for "${queryUsed}", ${brandName} is frequently recommended by AI systems. The brand appears prominently in recommendations and comparisons, indicating strong AI visibility...`,
  };
}

/**
 * Creates mock platform visibility result with provided brand and keyword
 */
export function createMockPlatformResult(brandName: string, targetKeyword: string): PlatformVisibilityResult {
  return {
    success: true,
    brandName,
    targetKeyword,
    platforms: mockPlatforms,
    summary: "🐛 DEBUG MODE - Mock platform visibility data",
  };
}

/**
 * Creates mock Google search result with provided brand and keyword
 */
export function createMockGoogleResult(brandName: string, targetKeyword: string): GoogleSearchResult {
  return {
    success: true,
    brandName,
    targetKeyword,
    platform: "Google Search",
    found: true,
    position: 5,
    context: "🐛 MOCK: Brand found in position 5 of organic search results",
    confidence: "medium",
    topCompetitors: ["Competitor Alpha", "Competitor Beta", "Competitor Gamma"],
    domainVerified: true,
    spellingCorrected: false,
    suggestedQuery: undefined,
  };
}

/**
 * Delay duration for simulating API response time in debug mode
 * Allows testing of loading states and animations
 */
export const MOCK_DELAY_MS = 2000;
